import { z } from "zod";
import { artistSchema } from "./artist";

/**
 * Wire-shape schemas for `user.managedArtists.*` endpoints. Single source
 * of truth — the trpc routers import these for `.output(...)` and the
 * client durable-cache layer (`apps/mobile/hooks/trpc/useManagedArtists.ts`)
 * imports them as the parse target.
 *
 * Existing per-route inline output schemas have been hoisted to this file.
 */

// `getManagedEntities` — entity ids grouped by entity type for the caller.
export const managedEntitiesWireSchema = z.object({
  artist: z.array(z.string()),
  composition: z.array(z.string()),
  recording: z.array(z.string()),
});
export type ManagedEntities = z.infer<typeof managedEntitiesWireSchema>;

// `getManagedArtists` — passes through to catalog `getArtistsByIds`.
export const managedArtistSchema = artistSchema.extend({
  partnerType: z.string().nullable().optional(),
});
export const managedArtistsWireSchema = z.array(managedArtistSchema);

// `searchArtist` — array of basic artist suggestions.
export const searchArtistResultWireSchema = z.object({
  id: z.string(),
  name: z.string(),
  image: z.string().nullable().optional(),
  blurhash: z.string().nullable().optional(),
  ext_followers: z.number().nullable().optional(),
  genre_names: z.array(z.string()).nullable().optional(),
  ext_streams: z.number().nullable().optional(),
});
export const searchArtistWireSchema = z.array(searchArtistResultWireSchema);

// `getSongsByManagedArtistIds` — record keyed by artist id.
export const songByManagedArtistWireSchema = z.object({
  id: z.string(),
  title: z.string(),
});
export const songsByManagedArtistsWireSchema = z.record(
  z.string(),
  z.array(songByManagedArtistWireSchema),
);

// `getStats` — monthly royalty stats.
export const statsWireSchema = z.object({
  your_share: z.number(),
  total_revenue: z.number(),
  songs: z.array(
    z.object({
      title: z.string(),
      artist: z.string(),
      streams: z.number(),
      stream_time: z.number(),
      your_share: z.number(),
      total_royalties: z.number(),
    }),
  ),
  by_date: z.record(
    z.string(),
    z.object({
      stream_time: z.number(),
      number_of_users: z.number(),
      number_of_streams: z.number(),
    }),
  ),
});
